import React, { useState } from 'react';
import Header from '../components/Header';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQPage: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      category: "General",
      question: "How does your doorstep repair service work?",
      answer: "Our doorstep service is simple - book an appointment, and our certified technician will visit your location at the scheduled time. They'll diagnose the issue, provide a quote, and if you approve, complete the repair right there."
    },
    {
      category: "General",
      question: "Which areas do you cover?",
      answer: "We currently provide services in Kalyan (East & West), Dombivli (East & West), and Palava City (Phase 1 & 2), including surrounding areas."
    },
    {
      category: "Repairs",
      question: "What types of mobile repairs do you handle?",
      answer: "We handle a wide range of repairs including screen replacement, battery replacement, charging port repairs, camera issues, speaker problems, software issues, and water damage treatment."
    },
    {
      category: "Repairs",
      question: "How long does a typical repair take?",
      answer: "Most repairs are completed within 30-60 minutes. However, some complex repairs might take longer. We'll provide an estimated time before starting the repair."
    },
    {
      category: "Warranty",
      question: "Do you provide warranty on repairs?",
      answer: "Yes, we provide a 90-day warranty on all our repairs. This covers any defects in parts or workmanship. Terms and conditions apply."
    },
    {
      category: "Parts",
      question: "Do you use genuine spare parts?",
      answer: "Yes, we use only genuine or high-quality compatible parts for all repairs to ensure optimal performance and longevity of your device."
    },
    {
      category: "Payment",
      question: "What payment methods do you accept?",
      answer: "We accept cash on delivery (COD) and various online payment methods including UPI, credit/debit cards, and net banking."
    },
    {
      category: "Booking",
      question: "How can I book a repair service?",
      answer: "You can book our service through our website, WhatsApp, or by calling us directly. Select your device, choose the required service, pick a convenient time slot, and provide your address."
    },
    {
      category: "Safety",
      question: "Is my data safe during repairs?",
      answer: "Yes, we take data privacy very seriously. Our technicians are trained to handle devices professionally, and we don't access any personal data during repairs."
    },
    {
      category: "Pricing",
      question: "How is the repair cost calculated?",
      answer: "The cost includes the price of spare parts (if needed) and service charges. We provide transparent pricing with no hidden costs, and you'll know the exact amount before we start the repair."
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="FAQ" />
      
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">
            Frequently Asked Questions
          </h1>

          <div className="space-y-6">
            {categories.map((category, categoryIndex) => (
              <div key={category}>
                <h2 className="text-xl font-medium text-gray-800 mb-4">{category}</h2>
                <div className="space-y-3">
                  {faqs
                    .filter(faq => faq.category === category)
                    .map((faq, index) => {
                      const faqIndex = categoryIndex * 100 + index;
                      return (
                        <div
                          key={faqIndex}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                            onClick={() => setActiveIndex(activeIndex === faqIndex ? null : faqIndex)}
                          >
                            <span className="font-medium text-gray-900">{faq.question}</span>
                            <i className={`fas fa-chevron-down text-gray-500 transition-transform ${
                              activeIndex === faqIndex ? 'transform rotate-180' : ''
                            }`}></i>
                          </button>
                          {activeIndex === faqIndex && (
                            <div className="px-4 py-3 bg-white">
                              <p className="text-gray-600">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Still have questions?</h2>
            <p className="text-gray-600 mb-4">
              We're here to help. Contact us through any of these channels:
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+919324299258"
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-minto-500 transition-colors"
              >
                <i className="fas fa-phone text-minto-500"></i>
                <span className="text-gray-900">Call Us</span>
              </a>
              <a
                href="https://wa.me/+919324299258"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-green-500 transition-colors"
              >
                <i className="fab fa-whatsapp text-green-500"></i>
                <span className="text-gray-900">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage; 